import type { ArticlePublicPathInput } from './unified';

// Coach routes — served from the site root, like the other root-served pages.
// The coach shell — the parent route every in-app surface will live under. `/dashboard` is the
// name it was born with and still answers to: pages/unified/dashboard.vue redirects here with the
// query string untouched.
export const COACH_ROUTE_INDEX = '/coach';
// The coach's ambient surfaces: child routes of the shell, so opening one is a navigation the
// student can link to, reload and walk back out of, and none of them unmounts the shell (which
// starts a coach session every time it mounts). The URL noun is the student's word for the
// surface, which is not always the activity's: badges live in what the code calls the wallet.
export const COACH_ROUTE_ACCOUNT = `${COACH_ROUTE_INDEX}/account`;
export const COACH_ROUTE_BADGES = `${COACH_ROUTE_INDEX}/badges`;
export const COACH_ROUTE_BADGE = (slug: string) => `${COACH_ROUTE_BADGES}/${slug}`;
// The new information architecture's Explore and Plan, which a student has only while it applies.
export const COACH_ROUTE_EXPLORE = `${COACH_ROUTE_INDEX}/explore`;
export const COACH_ROUTE_PLAN = `${COACH_ROUTE_INDEX}/plan`;
// The student's Replay and its share settings, as Plan opens them inside the shell. A link meant for
// someone else is the share's own public URL (`/cdp/:token`), never one of these.
export const COACH_ROUTE_CDP = `${COACH_ROUTE_INDEX}/cdp`;
export const COACH_ROUTE_CDP_SHARE_SETTINGS = `${COACH_ROUTE_CDP}/share-settings`;
// The public pages the IA's places open, each at the coach equivalent of its public path, which a
// student has only while the IA applies: a course, an industry hub, an article and a video.
export const COACH_ROUTE_COURSES = `${COACH_ROUTE_INDEX}/courses`;
export const COACH_ROUTE_COURSE = (id: string) => `${COACH_ROUTE_COURSES}/${id}`;
export const COACH_ROUTE_CAREERS = `${COACH_ROUTE_INDEX}/career-advice`;
export const COACH_ROUTE_HUB = (considerationAreaSlug: string) => `${COACH_ROUTE_CAREERS}/${considerationAreaSlug}`;
export const COACH_ROUTE_ARTICLES = `${COACH_ROUTE_INDEX}/articles`;
// An article lives under its first consideration area's slug, or under /coach/articles when it has
// no slugged area, as its public page does (`articlePublicPath`).
export const coachArticlePath = (article: ArticlePublicPathInput): string => {
  const considerationAreaSlug = article.considerationAreas?.[0]?.slug;
  return considerationAreaSlug ? `${COACH_ROUTE_HUB(considerationAreaSlug)}/${article.slug}` : `${COACH_ROUTE_ARTICLES}/${article.slug}`;
};
export const COACH_ROUTE_VIDEOS = `${COACH_ROUTE_INDEX}/videos`;
export const COACH_ROUTE_VIDEO = ({ slug, hubSlug }: { slug: string; hubSlug?: string }) =>
  `${COACH_ROUTE_VIDEOS}/${slug}${hubSlug ? `?hub=${encodeURIComponent(hubSlug)}` : ''}`;
// The entity surfaces, mirroring the public paths a signed-out student would be sent to. A single
// entity is a segment (`/coach/jobs/software-developers`), a set of them is the same page listed
// (`/coach/jobs?ids=…`), and a piece of content nests under the collection it belongs to.
export const COACH_ROUTE_JOBS = `${COACH_ROUTE_INDEX}/jobs`;
export const COACH_ROUTE_JOB = (slug: string) => `${COACH_ROUTE_JOBS}/${slug}`;
export const COACH_ROUTE_INSTITUTIONS = `${COACH_ROUTE_INDEX}/institutions`;
export const COACH_ROUTE_INSTITUTION = (slug: string) => `${COACH_ROUTE_INSTITUTIONS}/${slug}`;
// The student's shortlisted jobs ("My pathways"), each with the routes into it; one job's routes
// compared side by side live under a segment (`/coach/saved-pathways/<occupationId>`). The URL
// noun is the student's word; the code and the coach's navigation vocabulary keep saying
// job-candidates, and `/coach/job-candidates{,/*}` redirects here for the links already out there.
export const COACH_ROUTE_JOB_CANDIDATES = `${COACH_ROUTE_INDEX}/saved-pathways`;
export const COACH_ROUTE_LEARN = `${COACH_ROUTE_INDEX}/learn`;
export const COACH_ROUTE_LEARN_COLLECTION = (slug: string) => `${COACH_ROUTE_LEARN}/${slug}`;
// What the student's school has set them. Its own surface rather than a shelf in the library: the
// library launches content, and a lesson is work that has to be opened as a coach session.
export const COACH_ROUTE_LESSONS = `${COACH_ROUTE_INDEX}/lessons`;
export const COACH_ROUTE_PORTFOLIO = `${COACH_ROUTE_INDEX}/portfolio`;
export const COACH_ROUTE_PROFILE = `${COACH_ROUTE_INDEX}/profile`;
export const COACH_ROUTE_REWARDS = `${COACH_ROUTE_INDEX}/rewards`;
export const COACH_ROUTE_SAVED = `${COACH_ROUTE_INDEX}/saved`;
